import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  Button,
  Spinner,
  Alert,
  EmptyState,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@glplus/glpweb-dsl';
import { useCapabilities } from '../lib/useCapabilities';
import {
  adminApiClient,
  type JobCatalogItem,
  type GlobalJobRunItem,
  type RunJobResult,
} from '../lib/adminApiClient';
import { NEUTRAL_BTN } from '../lib/buttonStyles';
import { DomainChip, ScheduleChip, JobStatusChip } from '../components/JobChips';

/**
 * Background-job runner (capability job:run). Two views via a Jobs/History
 * toggle (Figma "Admin — Background jobs" 345:2 / run history 359:2):
 *  - Jobs: the catalog grouped/filterable by domain, each row showing its last
 *    run + a Run action that opens the run dialog.
 *  - History: the recent-run feed across all jobs.
 * Destructive jobs (●) require typing the job name to confirm before running.
 */

const JOBS_GRID = '2fr 1fr 0.9fr 1.2fr 0.9fr 0.7fr';
const HIST_GRID = '1.6fr 0.9fr 0.7fr 1.3fr 1fr';
const ALL = '__all__';

function relTime(iso: string | null): string {
  if (!iso) return 'Never';
  const then = new Date(iso).getTime();
  const s = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (s < 45) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}
function fmtStarted(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
function fmtDuration(ms: number | null, status: string): string {
  if (status === 'running') return '—';
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  const s = ms / 1000;
  if (s < 60) return `${s % 1 === 0 ? s : s.toFixed(s < 10 ? 1 : 0)}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${Math.round(s % 60)}s`;
}
function whoLabel(run: { triggerSource: string; triggeredBy: string | null }): string {
  if (run.triggerSource === 'admin') return run.triggeredBy ? 'admin' : 'admin';
  return run.triggerSource; // 'cron' | 'manual'
}
function summarySnippet(summary: unknown): string {
  if (summary == null) return '';
  let obj = summary;
  if (typeof obj === 'object' && obj && 'body' in obj && typeof (obj as { body: unknown }).body === 'string') {
    try {
      obj = JSON.parse((obj as { body: string }).body);
    } catch {
      /* keep */
    }
  }
  if (typeof obj === 'object' && obj) {
    const parts = Object.entries(obj as Record<string, unknown>)
      .filter(([k]) => !['statusCode', 'body', 'ms', 'timestamp'].includes(k))
      .slice(0, 3)
      .map(([k, v]) => `${k}: ${typeof v === 'object' ? '…' : String(v)}`);
    return parts.join(' · ');
  }
  return String(obj).slice(0, 60);
}

function Centered({ children }: { children: React.ReactNode }) {
  return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>{children}</Box>;
}

const DestructiveDot = () => (
  <Box component="span" sx={{ color: '#b91c1c', fontSize: '10px', lineHeight: 1, mr: 0.75 }}>
    ●
  </Box>
);

function Toggle({ tab, setTab }: { tab: 'jobs' | 'history'; setTab: (t: 'jobs' | 'history') => void }) {
  const seg = (key: 'jobs' | 'history', label: string) => (
    <Box
      role="button"
      onClick={() => setTab(key)}
      sx={{
        px: 2,
        py: 0.75,
        borderRadius: '6px',
        cursor: 'pointer',
        fontSize: '13px',
        fontWeight: tab === key ? 700 : 600,
        color: tab === key ? '#1f2937' : '#6b7280',
        backgroundColor: tab === key ? '#fff' : 'transparent',
        boxShadow: tab === key ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
      }}
    >
      {label}
    </Box>
  );
  return (
    <Box sx={{ display: 'flex', gap: 0.5, p: 0.5, backgroundColor: '#eceff2', borderRadius: '8px' }}>
      {seg('jobs', 'Jobs')}
      {seg('history', 'History')}
    </Box>
  );
}

export default function JobRunner() {
  const caps = useCapabilities();
  const canView = caps.has('job:read');
  const canRun = caps.has('job:run');

  const [tab, setTab] = useState<'jobs' | 'history'>('jobs');

  // ── Jobs list ──
  const [jobs, setJobs] = useState<JobCatalogItem[]>([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [jobsError, setJobsError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [domain, setDomain] = useState(ALL);
  const [schedule, setSchedule] = useState(ALL);
  const [statusF, setStatusF] = useState(ALL);

  const loadJobs = useCallback(async () => {
    setJobsLoading(true);
    setJobsError(null);
    try {
      const res = await adminApiClient.listJobs();
      setJobs(res.items);
    } catch (err) {
      setJobsError(err instanceof Error ? err.message : 'Failed to load jobs');
    } finally {
      setJobsLoading(false);
    }
  }, []);

  // ── History feed ──
  const [runs, setRuns] = useState<GlobalJobRunItem[]>([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [runsError, setRunsError] = useState<string | null>(null);

  const loadRuns = useCallback(async () => {
    setRunsLoading(true);
    setRunsError(null);
    try {
      const res = await adminApiClient.getAllJobRuns(40);
      setRuns(res.items);
    } catch (err) {
      setRunsError(err instanceof Error ? err.message : 'Failed to load run history');
    } finally {
      setRunsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!canView) return;
    if (tab === 'jobs') void loadJobs();
    else void loadRuns();
  }, [canView, tab, loadJobs, loadRuns]);

  const domainOptions = useMemo(() => {
    const seen = [...new Set(jobs.map((j) => j.domain))].sort();
    return [{ value: ALL, label: 'All domains' }, ...seen.map((d) => ({ value: d, label: d }))];
  }, [jobs]);

  const visibleJobs = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return jobs
      .filter((j) => (domain === ALL ? true : j.domain === domain))
      .filter((j) => (schedule === ALL ? true : j.schedule === schedule))
      .filter((j) => (statusF === ALL ? true : j.lastStatus === statusF))
      .filter(
        (j) =>
          !needle ||
          j.name.toLowerCase().includes(needle) ||
          j.description.toLowerCase().includes(needle),
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [jobs, search, domain, schedule, statusF]);

  // ── Run dialog ──
  const [runTarget, setRunTarget] = useState<JobCatalogItem | null>(null);
  const [paramsText, setParamsText] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState<RunJobResult | null>(null);
  const [runError, setRunError] = useState<string | null>(null);

  const openRun = (job: JobCatalogItem) => {
    setRunTarget(job);
    setParamsText('');
    setConfirmText('');
    setRunResult(null);
    setRunError(null);
  };
  const closeRun = () => setRunTarget(null);

  const confirmOk = !runTarget?.destructive || confirmText.trim() === runTarget?.name;

  const doRun = async () => {
    if (!runTarget) return;
    let params: Record<string, unknown> | undefined;
    if (paramsText.trim()) {
      try {
        params = JSON.parse(paramsText);
      } catch {
        setRunError('Parameters must be valid JSON.');
        return;
      }
    }
    setRunning(true);
    setRunError(null);
    setRunResult(null);
    try {
      const res = await adminApiClient.runJob(runTarget.name, {
        params,
        confirm: runTarget.destructive ? confirmText.trim() : undefined,
      });
      setRunResult(res);
    } catch (err) {
      setRunError(err instanceof Error ? err.message : 'Run failed');
    } finally {
      setRunning(false);
    }
  };

  function resultIsOk(r: RunJobResult): boolean {
    if (r.invokeStatus !== 200 || r.functionError) return false;
    const p = r.result as { statusCode?: number } | null;
    if (p && typeof p === 'object' && typeof p.statusCode === 'number' && p.statusCode >= 400) return false;
    return true;
  }

  if (caps.loading) {
    return (
      <Centered>
        <Spinner color="brand" />
      </Centered>
    );
  }
  if (!canView) {
    return (
      <Box sx={{ p: 1 }}>
        <Alert severity="error">You don&apos;t have permission to view background jobs.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
        <Typography variant="h4" sx={{ fontWeight: 800, color: '#1f2937' }}>
          Background jobs
        </Typography>
        <Toggle tab={tab} setTab={setTab} />
      </Box>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, maxWidth: 760 }}>
        {tab === 'jobs'
          ? 'Run any of the background data jobs on demand, watch the result, and review run history. Scheduled jobs also appear here. Destructive jobs (●) require typed confirmation.'
          : 'Every job run — scheduled and on-demand — with status, duration, who triggered it, and a result summary. Newest first.'}
      </Typography>

      {!canRun && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="info">View-only access — you can browse but can’t make changes.</Alert>
        </Box>
      )}

      {tab === 'jobs' ? (
        <JobsView
          canWrite={canRun}
          search={search}
          setSearch={setSearch}
          domain={domain}
          setDomain={setDomain}
          schedule={schedule}
          setSchedule={setSchedule}
          statusF={statusF}
          setStatusF={setStatusF}
          domainOptions={domainOptions}
          loading={jobsLoading}
          error={jobsError}
          jobs={visibleJobs}
          total={jobs.length}
          openRun={openRun}
        />
      ) : (
        <HistoryView loading={runsLoading} error={runsError} runs={runs} />
      )}

      {/* Run dialog (+ destructive guard) */}
      <Dialog open={!!runTarget} onClose={closeRun} maxWidth="sm" fullWidth paperBorderRadius={16}>
        {runTarget && (
          <>
            <DialogTitle>
              {runTarget.destructive && (
                <Typography
                  sx={{ color: '#b91c1c', fontWeight: 700, fontSize: '11px', letterSpacing: 0.6, mb: 0.5 }}
                >
                  ▲ DESTRUCTIVE ACTION
                </Typography>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography sx={{ fontWeight: 800, fontSize: '18px', color: '#1f2937' }}>
                  {runTarget.name}
                </Typography>
                <DomainChip domain={runTarget.domain} />
                <ScheduleChip schedule={runTarget.schedule} />
              </Box>
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                {runTarget.description}
              </Typography>
            </DialogTitle>
            <DialogContent>
              {runTarget.destructive && (
                <Box
                  sx={{
                    backgroundColor: '#fef2f2',
                    border: '1px solid #fecaca',
                    borderRadius: 2,
                    p: 1.5,
                    mb: 2,
                  }}
                >
                  <Typography variant="body2" sx={{ color: '#7f1d1d' }}>
                    This job is destructive and may be irreversible. To confirm, type the job name
                    below.
                  </Typography>
                </Box>
              )}

              <Typography sx={{ fontWeight: 700, fontSize: '12px', color: '#6b7280', mb: 0.5 }}>
                Parameters (JSON)
              </Typography>
              <TextField
                ariaLabel="Job parameters as JSON"
                multiline
                minRows={3}
                size="small"
                placeholder={'{\n  "dryRun": true\n}'}
                value={paramsText}
                onChange={(e) => setParamsText(e.target.value)}
                sx={{ width: '100%', fontFamily: 'monospace' }}
              />
              <Typography variant="caption" sx={{ color: '#9aa1ab' }}>
                Optional — leave blank to run with defaults.
              </Typography>

              {runTarget.destructive && (
                <Box sx={{ mt: 2 }}>
                  <TextField
                    ariaLabel="Type the job name to confirm"
                    size="small"
                    placeholder={`Type ${runTarget.name}…`}
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    sx={{ width: '100%' }}
                  />
                </Box>
              )}

              {runError && (
                <Box sx={{ mt: 2 }}>
                  <Alert severity="error">{runError}</Alert>
                </Box>
              )}

              {runResult && (
                <Box
                  sx={{
                    mt: 2,
                    backgroundColor: resultIsOk(runResult) ? '#ecfdf5' : '#fef2f2',
                    border: `1px solid ${resultIsOk(runResult) ? '#a7f3d0' : '#fecaca'}`,
                    borderRadius: 2,
                    p: 1.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: resultIsOk(runResult) ? '#15803d' : '#b91c1c',
                      mb: 0.5,
                    }}
                  >
                    {resultIsOk(runResult) ? '✓ Success' : '✕ Failed'}
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      fontSize: '12px',
                      color: '#334155',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      maxHeight: 200,
                      overflow: 'auto',
                    }}
                  >
                    {JSON.stringify(runResult.functionError ?? runResult.result, null, 2)}
                  </Box>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                ariaLabel="Cancel"
                buttonText={runResult ? 'Close' : 'Cancel'}
                variant="outlined"
                size="small"
                sx={NEUTRAL_BTN}
                onClick={closeRun}
              />
              {!runResult && canRun && (
                <Button
                  ariaLabel={`Run ${runTarget.name}`}
                  buttonText={running ? 'Running…' : 'Run job'}
                  variant="contained"
                  size="small"
                  disabled={running || !confirmOk}
                  sx={{
                    backgroundColor: runTarget.destructive ? '#b91c1c' : '#f06500',
                    '&:hover': { backgroundColor: runTarget.destructive ? '#991b1b' : '#d35400' },
                  }}
                  onClick={() => void doRun()}
                />
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}

// ── Jobs table ──
function JobsView(props: {
  canWrite: boolean;
  search: string;
  setSearch: (v: string) => void;
  domain: string;
  setDomain: (v: string) => void;
  schedule: string;
  setSchedule: (v: string) => void;
  statusF: string;
  setStatusF: (v: string) => void;
  domainOptions: { value: string; label: string }[];
  loading: boolean;
  error: string | null;
  jobs: JobCatalogItem[];
  total: number;
  openRun: (j: JobCatalogItem) => void;
}) {
  const { jobs, openRun, canWrite } = props;
  return (
    <>
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Search jobs by name"
          ariaLabel="Search jobs"
          size="small"
          value={props.search}
          onChange={(e) => props.setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 220 }}
        />
        <Select
          ariaLabel="Filter by domain"
          label="Domain"
          native
          size="small"
          sx={{ width: 170 }}
          value={props.domain}
          onChange={(_e, v) => props.setDomain(v as string)}
          options={props.domainOptions}
        />
        <Select
          ariaLabel="Filter by schedule"
          label="Schedule"
          native
          size="small"
          sx={{ width: 160 }}
          value={props.schedule}
          onChange={(_e, v) => props.setSchedule(v as string)}
          options={[
            { value: ALL, label: 'All schedules' },
            { value: 'cron', label: 'Cron' },
            { value: 'on-demand', label: 'On-demand' },
          ]}
        />
        <Select
          ariaLabel="Filter by status"
          label="Status"
          native
          size="small"
          sx={{ width: 150 }}
          value={props.statusF}
          onChange={(_e, v) => props.setStatusF(v as string)}
          options={[
            { value: ALL, label: 'All statuses' },
            { value: 'success', label: 'Success' },
            { value: 'failure', label: 'Failed' },
            { value: 'running', label: 'Running' },
            { value: 'never', label: 'Never run' },
          ]}
        />
      </Box>

      {props.error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{props.error}</Alert>
        </Box>
      )}

      {props.loading ? (
        <Centered>
          <Spinner color="brand" />
        </Centered>
      ) : jobs.length === 0 ? (
        <EmptyState icon="recurring" title="No jobs" body="No jobs match this filter." />
      ) : (
        <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden', backgroundColor: '#fff' }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: JOBS_GRID,
              gap: 2,
              px: 2,
              py: 1.25,
              backgroundColor: '#f8fafc',
              borderBottom: '1px solid #e2e8f0',
            }}
          >
            {['Job', 'Domain', 'Schedule', 'Last run', 'Status', ''].map((h, i) => (
              <Typography key={i} variant="caption" sx={{ fontWeight: 700, color: '#64748b', letterSpacing: 0.4 }}>
                {h.toUpperCase()}
              </Typography>
            ))}
          </Box>
          {jobs.map((job, idx) => (
            <Box
              key={job.name}
              sx={{
                display: 'grid',
                gridTemplateColumns: JOBS_GRID,
                gap: 2,
                px: 2,
                py: 1.5,
                alignItems: 'center',
                borderBottom: idx < jobs.length - 1 ? '1px solid #eef2f6' : 'none',
                '&:hover': { backgroundColor: '#fafbfc' },
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, color: '#1f2937' }}>
                  {job.destructive && <DestructiveDot />}
                  {job.name}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {job.description}
                </Typography>
              </Box>
              <Box>
                <DomainChip domain={job.domain} />
              </Box>
              <Box>
                <ScheduleChip schedule={job.schedule} />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ color: job.lastRun ? '#1f2937' : '#9aa1ab' }}>
                  {relTime(job.lastRun?.startedAt ?? null)}
                </Typography>
                {job.lastRun && (
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {whoLabel(job.lastRun)}
                  </Typography>
                )}
              </Box>
              <Box>
                <JobStatusChip status={job.lastStatus} />
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                {canWrite && (
                  <Button
                    ariaLabel={`Run ${job.name}`}
                    buttonText="Run"
                    variant="outlined"
                    size="small"
                    sx={
                      job.destructive
                        ? { ...NEUTRAL_BTN, color: '#b91c1c', borderColor: '#fca5a5' }
                        : NEUTRAL_BTN
                    }
                    onClick={() => openRun(job)}
                  />
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </>
  );
}

// ── History feed ──
function HistoryView(props: { loading: boolean; error: string | null; runs: GlobalJobRunItem[] }) {
  const { runs } = props;
  if (props.error) {
    return <Alert severity="error">{props.error}</Alert>;
  }
  if (props.loading) {
    return (
      <Centered>
        <Spinner color="brand" />
      </Centered>
    );
  }
  if (runs.length === 0) {
    return <EmptyState icon="recurring" title="No runs yet" body="Run a job to see it here." />;
  }
  return (
    <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden', backgroundColor: '#fff' }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: HIST_GRID,
          gap: 2,
          px: 2,
          py: 1.25,
          backgroundColor: '#f8fafc',
          borderBottom: '1px solid #e2e8f0',
        }}
      >
        {['Job', 'Status', 'Duration', 'Started', 'Triggered by'].map((h, i) => (
          <Typography key={i} variant="caption" sx={{ fontWeight: 700, color: '#64748b', letterSpacing: 0.4 }}>
            {h.toUpperCase()}
          </Typography>
        ))}
      </Box>
      {runs.map((run, idx) => (
        <Box
          key={run.id}
          sx={{
            display: 'grid',
            gridTemplateColumns: HIST_GRID,
            gap: 2,
            px: 2,
            py: 1.5,
            alignItems: 'center',
            borderBottom: idx < runs.length - 1 ? '1px solid #eef2f6' : 'none',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, color: '#1f2937' }}>{run.jobName}</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {run.errorMessage ? run.errorMessage.slice(0, 60) : summarySnippet(run.summary)}
            </Typography>
          </Box>
          <Box>
            <JobStatusChip status={run.status} />
          </Box>
          <Typography variant="body2" sx={{ color: run.status === 'running' ? '#9aa1ab' : '#1f2937' }}>
            {fmtDuration(run.durationMs, run.status)}
          </Typography>
          <Typography variant="body2" sx={{ color: '#1f2937' }}>
            {fmtStarted(run.startedAt)}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {whoLabel(run)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}
